import { ApiErrorPayload } from 'src/api/axiosClient/types';

/**
 * The axios client rejects with the response body, not with the AxiosError, so there is no `response.status`
 * to read here: the status arrives as `code` inside the payload.
 */
export const errorStatus = (error: unknown): number | null => {
  const code = (error as ApiErrorPayload | undefined)?.code;
  if (typeof code === 'number') return code;
  if (typeof code === 'string') {
    const parsed = Number(code);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

/**
 * Kinds whose message the API authors for the caller. Everything else carries text nobody wrote for a
 * reader — an internal 500 repeats the raw exception message, and a failure with no JSON body rejects with
 * the AxiosError itself ("Network Error"). These screens are open to anyone, and they suppress the generic
 * toast, so whatever this returns is what a visitor reads.
 */
const READABLE_ERROR_KINDS = ['validation', 'too_many_requests', 'email_taken'];

/**
 * Whether the API judged the request rather than failing on the way to it. The same request sent again gets
 * the same answer, so a screen has nothing to gain by offering it a second time — while a network failure or
 * a 500 is worth another press.
 */
export const isRefusal = (error: unknown): boolean =>
  (error as ApiErrorPayload | undefined)?.error === 'validation';

// The address on the funnel already belongs to an account. See `SignupCompanyInfoBlock` for why it is told
// apart from every other refusal.
export const isEmailTaken = (error: unknown): boolean =>
  (error as ApiErrorPayload | undefined)?.error === 'email_taken';

const TRANSIENT_STATUSES = [408, 429];

/**
 * Whether the same request has a chance of a different answer later. The missing status is the important half:
 * a network failure, a timeout and a CORS refusal all reject without one, so a screen that reads those as a
 * verdict acts on an answer nobody gave — and an error nobody can classify counts as transient for the same
 * reason.
 */
export const isTransientFailure = (error: unknown): boolean => {
  const status = errorStatus(error);
  return status == null || status >= 500 || TRANSIENT_STATUSES.includes(status);
};

export const errorMessage = (error: unknown, fallback: string): string => {
  const payload = error as ApiErrorPayload | undefined;
  if (payload?.error == null || !READABLE_ERROR_KINDS.includes(payload.error)) return fallback;

  const message = payload.message;
  return message != null && message.length > 0 ? message : fallback;
};
